import type { BusinessQuery, BusinessReport } from './business-types';
import type { MiningLedger } from './mining-types';

export type ISODate = string;

export interface Killmail {
  id: number;
  occurredAt: string;
  systemName: string;
  shipName: string;
  shipTypeId: number;
  victimName: string;
  victimId: number | null;
  outcome: 'loss' | 'kill' | 'reference';
  expeditionId: string | null;
  financeRecordId: string | null;
  actualCost: number | null;
  totalValue: number | null;
  destroyedValue: number | null;
  droppedValue: number | null;
  npc: boolean;
  solo: boolean;
  damageTaken: number;
  url: string;
  attackers: Array<{ name: string; shipName: string; damage: number; finalBlow: boolean }>;
  items: Array<{ name: string; typeId: number; destroyed: number; dropped: number; flag: number }>;
}

export interface Session {
  authenticated: boolean;
  csrfToken: string | null;
  isAdmin: boolean;
  characterId: number | null;
  canUseApp: boolean;
  corporationName: string;
}

export interface EveLoginStatus {
  configured: boolean;
  loginAvailable: boolean;
}

export interface AlertItem {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail: string;
  createdAt: ISODate;
}

export interface Overview {
  industryJobsActive: number;
  industryJobsReady: number;
  marketOrdersActive: number;
  walletBalance: number | null;
  inventoryValue: number | null;
  pendingSyncs: number;
  lastSyncAt: ISODate | null;
  alerts: AlertItem[];
  investableISK?: number;
  budgetSource?: string;
}

export interface Character {
  id: number;
  name: string;
  corporationName: string | null;
  portraitUrl: string | null;
  lastSyncAt: ISODate | null;
  status: 'active' | 'linked' | 'expired' | 'error';
}

export interface IndustryJob {
  id: number;
  productName: string;
  activity: string;
  status: string;
  runs: number;
  endDate: ISODate | null;
  facilityName: string | null;
  progress: number | null;
}

export interface MarketOpportunity {
  iskPerSlotHour?: number | null;
  averageDailyVolume30d?: number | null;
  typeId: number;
  typeName: string;
  blueprintTypeId: number;
  batchSize: number;
  materials: Array<{ typeId: number; name: string; required: number; missing: number; unitPrice: number | null }>;
  materialCost: number;
  jobCost: number;
  netProfit: number | null;
  marginPercent: number | null;
  capitalRequired: number | null;
  durationSeconds: number;
  breakEven: number | null;
  saleScenario: string;
  buyScenario: string;
  eligible: boolean;
  reason: string | null;
  confidence: string;
  liquidityScope: 'The Forge regional historical volume; Jita order book';
  volume24h: number | null;
  updatedAt: ISODate;
  ownedBlueprint?: boolean;
  totalCost?: number | null;
  cashRequired?: number | null;
  economicCapitalRequired?: number | null;
  candidateSource?: string;
  blueprintAvailable?: boolean;
  blueprintItemId?: number;
  costBasisComplete?: boolean;
  outputUnits?: number;
}

export interface MarketOrder {
  id: string;
  typeId: number;
  typeName: string;
  side: 'sell' | 'buy';
  state: string;
  issuedAt: ISODate;
  expiresAt: ISODate | null;
  price: number;
  volumeTotal: number;
  volumeRemain: number;
  filledVolume: number;
  remainingValue: number;
  escrow: number;
  locationId: string;
  locationName: string;
  regionId: number | null;
  walletDivision: number | null;
}

export interface MarketLedgerEntry {
  id: string;
  source: 'transaction' | 'journal';
  sourceId: string;
  date: ISODate;
  type: 'sale' | 'purchase' | 'fee' | 'adjustment';
  typeId: number | null;
  typeName: string | null;
  quantity: number | null;
  unitPrice: number | null;
  amount: number;
  locationId: string | null;
  locationName: string | null;
  refType: string | null;
  description: string;
  journalRefId: string | null;
  balance: number | null;
  counterpartyId: string | null;
}

export interface MarketSource {
  module: string;
  label: string;
  status: 'current' | 'stale' | 'missing_scope' | 'pending' | 'error';
  lastSyncAt: ISODate | null;
  detail: string | null;
}

export interface MarketManagement {
  orders: MarketOrder[];
  ledger: MarketLedgerEntry[];
  sources: MarketSource[];
  summary: {
    activeSellOrders: number;
    activeBuyOrders: number;
    sellRemainingValue: number;
    buyEscrow: number;
    salesGross: number;
    purchasesGross: number;
    fees: number;
    netCashflow: number;
  };
  coverage: {
    firstCapturedAt: ISODate | null;
    lastCapturedAt: ISODate | null;
    transactionCount: number;
    journalCount: number;
    notice: string;
  };
  readOnly: true;
}

export interface Blueprint {
  itemId?: number;
  locationId?: number;
  resolvedLocationId?: number | null;
  systemId?: number | null;
  characterId?: number;
  characterName?: string;
  sourceStatus?: string;
  sourceFetchedAt?: ISODate | null;
  sourceExpiresAt?: ISODate | null;
  quantity?: number;
  available?: boolean;
  activeJobId?: number | null;
  availabilityReason?: string | null;
  jobsSourceStatus?: string;
  typeId: number;
  name: string;
  original: boolean;
  materialEfficiency: number;
  timeEfficiency: number;
  runs: number | null;
  locationName: string | null;
}

export interface BlueprintResearchInput {
  blueprintTypeId: number;
  runs: number;
  currentME: number;
  targetME: number;
  researchCost: number;
  researchHours: number;
}

export interface BlueprintResearchResult {
  savedMaterials: Array<{ typeId: number; name: string; savedQuantity: number; estimatedSavings: number | null }>;
  savedCost: number | null;
  paybackRuns: number | null;
  worthwhile: boolean;
  assumptions: string[];
}

export interface InventoryItem {
  locationResolved?: boolean;
  storageName?: string;
  storageKind?: 'hangar' | 'station_container' | 'ship' | 'container' | 'other' | 'unknown';
  isIndustryMaterial?: boolean;
  categoryId?: number | null;
  availableInHangar?: boolean | null;
  locationFlag?: string;
  locationId?: number;
  resolvedLocationId?: number | null;
  systemId?: number | null;
  groupName?: string | null;
  categoryName?: string | null;
  characterName?: string;
  typeId: number;
  name: string;
  quantity: number;
  locationName: string;
  estimatedUnitPrice: number | null;
  estimatedValue: number | null;
  updatedAt: ISODate | null;
}

export interface FinanceSummary {
  walletBalance: number | null;
  income30d: number;
  expenses30d: number;
  net30d: number;
  industryCommitted: number;
  capitalAvailable: number;
}

export interface FinanceHistorySample {
  capturedAt: ISODate;
  net30d: number;
  income30d: number;
  expenses30d: number;
  realizedIndustrialProfit: number | null;
  budgetSource: string;
}

export interface FinanceRecord {
  id: string;
  date: ISODate;
  type: 'income' | 'expense';
  amount: number;
  category: string;
  description: string;
  createdAt: ISODate;
}

export interface CapitalSettings {
  availableCapital: number;
  reservePercent: number;
  maxPerOperation: number;
  brokerFeePercent: number;
  salesTaxPercent: number;
}

export interface ManufacturingCalculationInput {
  blueprintItemId?: number;
  blueprintCost?: number;
  facilityId?: number;
  blueprintTypeId: number;
  runs: number;
  materialEfficiency: number;
  facilityTaxPercent: number;
  systemCostIndexPercent?: number;
  timeEfficiency?: number;
  industryLevel?: number;
  advancedIndustryLevel?: number;
  facilityMaterialMultiplier?: number;
  facilityTimeMultiplier?: number;
  haulingCost?: number;
  saleScenario?: string;
  buyScenario?: string;
  manufacturingSystemId?: number;
  facilityName?: string;
}

export interface ManufacturingCalculation {
  blueprintCost?: number | null;
  costBasisComplete?: boolean;
  blueprintAvailable?: boolean;
  blueprintJobId?: number | null;
  pricingPending?: boolean;
  economicCapitalRequired?: number | null;
  blueprintItemId?: number;
  materialEfficiency?: number;
  timeEfficiency?: number;
  ownedMaterialsValue?: number | null;
  missingMaterialsCost?: number | null;
  materialsCashRequired?: number | null;
  cashRequired?: number | null;
  jobCostBreakdown?: Record<string, number | null>;
  systemCostIndexPercent?: number;
  facilityTaxPercent?: number;
  surchargePercent?: number;
  eligible?: boolean;
  reason?: string | null;
  confidence?: string;
  assumptions?: string[];
  missingSkills?: Array<{ typeId: number; name: string; level: number }>;
  materialsCost: number | null;
  jobCost: number;
  taxes: number | null;
  totalCost: number | null;
  costPerUnit: number | null;
  netProfit?: number | null;
  marginPercent?: number | null;
  estimatedSaleValue?: number | null;
  durationSeconds?: number;
  breakEven?: number | null;
  typeName?: string;
  outputUnits?: number;
  facilityName?: string;
  capitalRequired?: number | null;
  haulingCost?: number;
  ownedBlueprint?: boolean;
  materials?: Array<{
    typeId: number;
    name: string;
    required: number;
    missing: number;
    ownedAtFacility: number;
    ownedTotal?: number;
    ownedAtLocation?: number;
    ownedInStationContainers?: number;
    ownedInShips?: number;
    requiresMove?: boolean;
    missingCost?: number | null;
    unitPrice: number | null;
    totalCost: number | null;
  }>;
}

export interface InventionBlueprint {
  blueprintTypeId: number;
  name: string;
  sourceCopyRuns: number[];
  products: Array<{ blueprintTypeId: number; name: string; baseProbability: number; licensedRuns: number }>;
  materials: Array<{ typeId: number; name: string; quantity: number }>;
  skills: Array<{ typeId: number; name: string; level: number }>;
}

export interface InventionDecryptor {
  typeId: number;
  name: string;
  probabilityMultiplier: number;
  runModifier: number;
  materialEfficiencyModifier: number;
  timeEfficiencyModifier: number;
}

export interface InventionInput {
  blueprintTypeId: number;
  productBlueprintTypeId: number;
  attempts: number;
  sourceCopyCost: number;
  installationCost: number;
  decryptorTypeId?: number;
  decryptorCost: number;
  sourceCopyRunsAvailable?: number;
  materialUnitPrices: Record<string, number>;
}

export interface InventionResult {
  success_probability: number | string;
  expected_successes: number | string;
  expected_cost_per_success: number | string | null;
  expected_cost_per_output_run: number | string | null;
  probability_no_success: number | string;
  output_bpc_runs: number;
  output_material_efficiency_percent: number;
  output_time_efficiency_percent: number;
  batch_cost: number | string;
  can_start: boolean;
  blocking_reasons: string[];
  assumptions: string[];
}

export interface AccountingEntry {
  typeId: number;
  typeName: string;
  quantity: number;
  unitCost: number | null;
  totalCost: number | null;
  method: string;
  updatedAt: ISODate;
  reconciled: boolean;
  actualAssetQuantity: number | null;
}

export interface ProductionBatch {
  id: string;
  name: string;
  productTypeId: number;
  outputQuantity: number;
  totalBasis: number;
  unitBasis: number;
  soldQuantity: number;
  realizedProfit: number;
  realizedRevenue: number;
}

export interface BatchInput {
  name: string;
  productTypeId: number;
  outputQuantity: number;
  materialCosts: Array<{ typeId: number; quantity: number; totalCost: number }>;
  jobCost: number;
  haulingCost: number;
}

export interface SettlementBudgetInput {
  emergencyReserve: number;
  operatingBuffer: number;
  committedCosts: number;
  projectCost: number;
  replacementReserve: number;
  costs: Record<string, number>;
}

export interface SettlementReadiness {
  liquid_isk: number | string;
  protected_capital: number | string;
  max_project_budget: number | string;
  project_required: number | string;
  ready: boolean;
  shortfall: number | string;
  liquidSource: string;
  operationalReadiness: string;
  recommendedStage: string;
}

export interface SkillQueueEntry {
  queue_position: number;
  skill_id: number;
  finished_level: number;
  start_date?: string;
  finish_date?: string;
}

export interface SkillItem {
  skillId: number;
  name: string;
  group: string;
  trainedLevel: number;
  activeLevel: number;
  skillpoints: number;
}

export interface SkillRecommendation {
  skillId: number;
  name: string;
  currentLevel: number;
  targetLevel: number;
  priority: 'high' | 'medium' | 'low';
  reason: string;
  estimatedTrainingSeconds: number | null;
}

export interface SkillPlan {
  readinessPercent: number | null;
  target: string;
  recommendations: SkillRecommendation[];
  assumptions: string[];
}

export type PiFlowNode =
  | { kind: 'extractor'; typeId: number | null; name: string; tier: number; count: number; heads: number; perHour: number; endsAt: ISODate | null; status: 'stopped' | 'soon' | 'running' }
  | { kind: 'factory'; typeId: number; name: string; tier: number; count: number; outputPerCycle: number; cycleSeconds: number }
  | { kind: 'storage'; typeId: number; name: string; role: 'import' | 'export' | null; fillPercent: number | null; contents: Array<{ typeId: number; name: string; amount: number }> };

export interface PiColony {
  colonyId: string;
  planetName: string;
  planetType: string;
  upgradeLevel: number | null;
  lastUpdate: ISODate | null;
  status: 'stopped' | 'soon' | 'running' | 'factory' | 'unknown';
  extractorEndsAt: ISODate | null;
  stoppedExtractors: number;
  soonExtractors: number;
  stages: PiFlowNode[][];
}

export interface PiColonies {
  colonies: PiColony[];
  production: Array<{ typeId: number; name: string; tier: number; perDay: number; colonies: Array<{ name: string; stopped: boolean }> }>;
}

export interface PiProduct {
  typeId: number;
  name: string;
  tier: number;
}

export interface PiChainNode {
  typeId: number;
  name: string;
  tier: number;
  required: number;
  covered: number;
  missing: number;
  cycles: number;
  colonies: string[];
  children: PiChainNode[];
}

export interface PiChainResult {
  root: PiChainNode;
  cycles: number;
  withoutColony: number;
  extraction: Array<{ typeId: number; name: string; quantity: number; planetTypes: string[] }>;
  planetTypes: string[];
  stockFetchedAt: ISODate | null;
  stockCurrent: boolean;
}

export interface Expedition {
  id: string;
  name: string;
  systemName: string;
  objective: string;
  budget: number;
  startsAt: ISODate;
  notes: string;
  status: 'planned' | 'active' | 'completed' | 'cancelled';
  createdAt: ISODate;
  durationHours?: number | null;
  lootEstimated?: number | null;
  realizedSales?: number | null;
  replacementCosts?: number | null;
  linkedKillmailCosts: number;
  totalReplacementCosts: number;
  killmailIds: number[];
  realizedProfit: number;
  estimatedProfit: number;
  ships?: string[];
  originSystem?: string | null;
  wormholeClass?: string | null;
}

export interface SettlementChecklistItem {
  id: string;
  label: string;
  completed: boolean;
}

export interface Settlement {
  id: string;
  name: string;
  systemName: string;
  budget: number;
  spent: number;
  status: 'planning' | 'deploying' | 'operational';
  checklist: SettlementChecklistItem[];
}

export interface WormholeConnection {
  id: string;
  fromSystem: string;
  toSystem: string | null;
  signature: string;
  type: string | null;
  massStatus: 'stable' | 'reduced' | 'critical' | 'unknown';
  lifeStatus: 'stable' | 'end_of_life' | 'unknown';
  discoveredAt: ISODate;
}

export interface AnoikisState {
  homeSystem: string | null;
  updatedAt: ISODate | null;
  riskLevel: 'low' | 'medium' | 'high' | 'unknown';
  activeConnections: number;
  notes: string[];
}

export interface ServiceState {
  status: 'ok' | 'healthy' | 'pending' | 'degraded' | 'down' | 'unavailable' | 'unknown';
  detail: string | null;
  lastSuccessAt: ISODate | null;
}

export type SynchronizationSourceStatus =
  | 'current'
  | 'pending'
  | 'stale'
  | 'error'
  | 'missing'
  | 'authorization_required'
  | 'deferred';

export interface SynchronizationSource {
  key: string;
  label?: string;
  status: SynchronizationSourceStatus | string;
  fetchedAt: ISODate | null;
  expiresAt: ISODate | null;
  error: string | null;
  detail?: string | null;
  action?: string | null;
  scope?: 'character' | 'market' | 'public' | string;
  retryAt?: ISODate | null;
}

export interface SynchronizationSummary {
  status: 'current' | 'pending' | 'degraded' | 'unavailable' | 'deferred' | string;
  workerStatus?: 'healthy' | 'running' | 'error' | 'unknown' | string;
  current: number;
  pending: number;
  stale: number;
  failed: number;
  missing: number;
  total: number;
  workerAlive: boolean;
  lastCycleAt: ISODate | null;
  lastRequestedAt: ISODate | null;
  requestPending: boolean;
  retryAt: ISODate | null;
  detail: string;
}

export interface AdminStatus {
  staticData?: {
    version?: string | number;
    importedAt?: string | number | null;
    status?: string;
    sha256?: string;
    source_url?: string;
  };
  backup?: {
    lastSuccess?: string | number | null;
    restoreValidated?: boolean;
    lastRestoreValidation?: string | number | null;
    encrypted?: boolean;
  };
  syncHealth?: SynchronizationSource[];
  sync?: SynchronizationSummary;
  version: string;
  environment: string;
  database: ServiceState;
  esi: ServiceState;
  scheduler: ServiceState;
  lastSyncAt: ISODate | null;
}

export interface SsoSettings {
  clientId: string | null;
  callbackUrl: string;
  configured: boolean;
  scopes: string[];
  availableScopes?: string[];
  flow?: string;
}

let csrfToken: string | null = null;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body) headers.set('Content-Type', 'application/json');
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: 'same-origin',
    headers,
  });
  if (!response.ok) {
    let message = `La solicitud falló (${response.status})`;
    try {
      const body = (await response.json()) as { detail?: string | Array<{ loc?: Array<string | number>; msg?: string }> };
      if (typeof body.detail === 'string') message = body.detail;
      else if (Array.isArray(body.detail)) message = body.detail.map((issue) => `${issue.loc?.filter((part) => part !== 'body').join('.') || 'Solicitud'}: ${issue.msg ?? 'Valor no válido'}`).join('; ');
    } catch {
      // The status still provides a useful, honest error when the body is not JSON.
    }
    throw new ApiError(message, response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function getSession(): Promise<Session> {
  const session = await api<Session>('/auth/session');
  csrfToken = session.csrfToken;
  return session;
}

export async function login(password: string): Promise<Session> {
  const session = await api<Session>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
  csrfToken = session.csrfToken;
  return session;
}

export async function logout(): Promise<void> {
  await api<void>('/auth/logout', { method: 'POST' });
  csrfToken = null;
}

export interface WorkflowBlueprint {
  typeId: number;
  name: string;
  activity: 'manufacturing' | 'reaction';
  runs: number;
  outputPerRun: number;
  materialEfficiency: number;
  bpo: number;
  bpc: number;
  bpcRuns: number;
  available: number;
}

export interface WorkflowNode {
  typeId: number;
  name: string;
  required: number;
  covered: number;
  missing: number;
  blueprint: WorkflowBlueprint | null;
  children: WorkflowNode[];
}

export interface WorkflowResult {
  root: WorkflowNode;
  buy: Array<{ typeId: number; name: string; quantity: number }>;
  blueprints: WorkflowBlueprint[];
  stockFetchedAt: ISODate | null;
  stockCurrent: boolean;
}

export const endpoints = {
  killmails: () => api<Killmail[]>('/killmails'),
  importKillmail: (reference: string) => api<Killmail>('/killmails/import', { method: 'POST', body: JSON.stringify({ reference }) }),
  updateKillmailTracking: (id: number, body: { expeditionId: string | null; actualCost?: number }) => api<Killmail>(`/killmails/${id}/tracking`, { method: 'PATCH', body: JSON.stringify(body) }),
  eveLoginStatus: () => api<EveLoginStatus>('/auth/eve/status'),
  startEveLogin: () => api<{ authorizeUrl: string }>('/auth/eve/login/start', { method: 'POST' }),
  accessSettings: () => api<{ operationsUrl: string; wandererUrl: string; wandererEntryUrl: string | null }>('/settings/access'),
  wandererAccess: () => api<{ entryConfigured: boolean }>('/settings/wanderer'),
  saveWandererAccess: (entryUrl: string) => api<{ entryConfigured: boolean }>('/settings/wanderer', { method: 'PUT', body: JSON.stringify({ entryUrl }) }),
  health: () => api<{ status: string }>('/health'),
  overview: () => api<Overview>('/overview'),
  characters: () => api<Character[]>('/characters'),
  industryJobs: () => api<IndustryJob[]>('/industry/jobs'),
  miningLedger: () => api<MiningLedger>('/industry/mining'),
  opportunities: () => api<MarketOpportunity[]>('/market/opportunities'),
  marketManagement: () => api<MarketManagement>('/market/management'),
  searchTypes: (query: string) => api<Array<{ id: number; name: string; groupName?: string; categoryName?: string }>>(`/types/search?q=${encodeURIComponent(query)}&limit=20`),
  industryCatalog: () => api<Array<{ blueprintTypeId: number; name: string; products: Array<{ typeID: number; quantity: number }> }>>('/industry/catalog'),
  blueprints: () => api<Blueprint[]>('/blueprints'),
  researchBlueprint: (body: BlueprintResearchInput) => api<BlueprintResearchResult>('/blueprints/research', { method: 'POST', body: JSON.stringify(body) }),
  workflowCatalog: () => api<Array<{ blueprintTypeId: number; name: string; activity: string }>>('/industry/workflow/catalog'),
  workflow: ({ blueprintTypeId, runs, me, ownedOnly }: { blueprintTypeId: number; runs: number; me?: number; ownedOnly: boolean }) => api<WorkflowResult>(`/industry/workflow?blueprintTypeId=${blueprintTypeId}&runs=${runs}${me === undefined ? '' : `&me=${me}`}&ownedOnly=${ownedOnly}`),
  inventionCatalog: () => api<InventionBlueprint[]>('/industry/invention/catalog'),
  inventionDecryptors: () => api<InventionDecryptor[]>('/industry/invention/decryptors'),
  planInvention: (body: InventionInput) => api<InventionResult>('/industry/invention/plan', { method: 'POST', body: JSON.stringify(body) }),
  inventory: () => api<InventoryItem[]>('/inventory'),
  financeSummary: () => api<FinanceSummary>('/finance/summary'),
  businessReport: (query: BusinessQuery = {}) => {
    const params = new URLSearchParams();
    if (query.start) params.set('start', query.start);
    if (query.end) params.set('end', query.end);
    if (query.scope) params.set('scope', query.scope);
    if (query.timezone) params.set('timezone', query.timezone);
    const suffix = params.toString();
    return api<BusinessReport>(`/finance/business${suffix ? `?${suffix}` : ''}`);
  },
  financeHistory: () => api<FinanceHistorySample[]>('/finance/history'),
  financeRecords: () => api<FinanceRecord[]>('/finance/records'),
  accounting: () => api<AccountingEntry[]>('/finance/accounting'),
  batches: () => api<ProductionBatch[]>('/finance/batches'),
  addBatch: (body: BatchInput) => api<ProductionBatch>('/finance/batches', { method: 'POST', body: JSON.stringify(body) }),
  sellBatch: (id: string, body: { quantity: number; grossRevenue: number; salesTax: number; brokerFee: number }) => api<ProductionBatch>(`/finance/batches/${encodeURIComponent(id)}/sales`, { method: 'POST', body: JSON.stringify(body) }),
  settlementBudget: () => api<SettlementBudgetInput>('/settlements/budget'),
  saveSettlementBudget: (body: SettlementBudgetInput) => api<SettlementReadiness>('/settlements/budget', { method: 'PUT', body: JSON.stringify(body) }),
  skillQueue: () => api<SkillQueueEntry[]>('/skills/queue'),
  capitalSettings: () => api<CapitalSettings>('/settings/capital'),
  skills: () => api<SkillItem[]>('/skills'),
  skillPlan: () => api<SkillPlan>('/skills/plan'),
  colonies: () => api<PiColonies>('/pi'),
  piSchematics: () => api<PiProduct[]>('/pi/schematics'),
  piChain: (typeId: number, quantity: number) => api<PiChainResult>(`/pi/chain?typeId=${typeId}&quantity=${quantity}`),
  expeditions: () => api<Expedition[]>('/expeditions'),
  settlements: () => api<Settlement[]>('/settlements'),
  wormholes: () => api<WormholeConnection[]>('/wormholes'),
  anoikis: () => api<AnoikisState>('/anoikis'),
  adminStatus: () => api<AdminStatus>('/admin/status'),
  sso: () => api<SsoSettings>('/admin/sso'),
  addFinanceRecord: (body: Omit<FinanceRecord, 'id' | 'createdAt'>) =>
    api<FinanceRecord>('/finance/records', { method: 'POST', body: JSON.stringify(body) }),
  saveCapitalSettings: (body: CapitalSettings) =>
    api<CapitalSettings>('/settings/capital', { method: 'PUT', body: JSON.stringify(body) }),
  calculateManufacturing: (body: ManufacturingCalculationInput) =>
    api<ManufacturingCalculation>('/calculator/manufacturing', { method: 'POST', body: JSON.stringify(body) }),
  addExpedition: (body: Omit<Expedition, 'id' | 'status' | 'createdAt' | 'linkedKillmailCosts' | 'totalReplacementCosts' | 'killmailIds' | 'realizedProfit' | 'estimatedProfit'>) =>
    api<Expedition>('/expeditions', { method: 'POST', body: JSON.stringify(body) }),
  updateExpedition: (id: string, body: Partial<Pick<Expedition, 'status' | 'durationHours' | 'lootEstimated' | 'realizedSales' | 'replacementCosts' | 'ships' | 'originSystem' | 'wormholeClass'>>) =>
    api<Expedition>(`/expeditions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  toggleSettlementItem: (settlementId: string, itemId: string) =>
    api<Settlement>(`/settlements/${encodeURIComponent(settlementId)}/checklist/${encodeURIComponent(itemId)}/toggle`, { method: 'POST' }),
  saveSso: (body: { clientId: string; clientSecret?: string; callbackUrl: string; scopes: string[] }) =>
    api<SsoSettings>('/admin/sso', { method: 'PUT', body: JSON.stringify(body) }),
  startSync: (scope: 'all' | 'market' | 'public' | 'character') => api<{ status: 'queued'; detail: string }>('/admin/sync', { method: 'POST', body: JSON.stringify({ scope }) }),
  startEveAuth: () => api<{ authorizeUrl: string }>('/auth/eve/start', { method: 'POST' }),
  startMiningAuth: () => api<{ authorizeUrl: string }>('/auth/eve/mining/start', { method: 'POST' }),
};
